"use client";

/**
 * Footer Renderer
 * Renders the page footer with optional logo + tagline, link columns
 * (manual + auto-generated "sections" column), and social icons.
 * Background is either the site primary color or a custom hex color;
 * text color auto-adjusts for contrast.
 */

import type React from "react";
import { useMemo } from "react";
import { SocialIcon } from "./_shared/SocialIcon";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import { useFooterPagesPublic } from "../../../_hooks/useFooterPagesPublic";
import { DEFAULT_FOOTER_CUSTOM_COLOR, DEFAULT_NAV_LABELS, FOOTER_ALIGNMENT_CLASSES } from "../../../_logic/constants";
import { resolveFooterAlignment } from "../../../_logic/footer-alignment";
import { isNavigableSection, isSafeUrl, isValidStorageUrl, getUniqueAnchorId } from "../../../_logic/utils";
import { resolveThemedSurface } from "../../../_logic/themed-surface";
import type { FooterComponent, FooterSocialLink, PageComponent } from "../../../_types";
import { useBuilderStore } from "../../../_stores/builder-store";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { SmartLink } from "../../public/SmartLink";
import { getResortMapForLandingSlug, resortMapSubpageHref } from "@/features/resort-map/_logic/registry";

interface FooterRendererProps {
  component: FooterComponent;
}

/** Builds {label, anchor} pairs for every visible non-footer, non-navbar section in page order */
function getSectionLinks(allComponents: PageComponent[]): { label: string; anchor: string }[] {
  return allComponents
    .filter((c) => c.type !== "navbar" && isNavigableSection(c))
    .map((c) => {
      const label = c.navigationLabel || DEFAULT_NAV_LABELS[c.type] || c.title;
      const anchor = getUniqueAnchorId(c, allComponents);
      return label && anchor ? { label, anchor } : null;
    })
    .filter((x): x is { label: string; anchor: string } => x !== null);
}

/** Returns the public href for a social link, or null if the value is unusable. */
function socialLinkHref(link: FooterSocialLink): string | null {
  if (!link.url) return null;
  if (link.type === "email") {
    // Allow either bare emails ("you@example.com") or explicit "mailto:" URIs.
    return link.url.startsWith("mailto:") ? link.url : `mailto:${link.url}`;
  }
  return isSafeUrl(link.url) ? link.url : null;
}

/**
 * Renders the page footer with theme-aware colors, optional auto-generated
 * sections column, manual link columns, and social icons.
 */
export function FooterRenderer({ component }: FooterRendererProps): React.ReactElement {
  // Hooks
  const { primaryColor, eventId, parentPageId } = useBuilderContext();
  const { isPublic } = usePublicViewContext();
  const allComponents = useBuilderStore((s) => s.components);

  // States — fetched via shared hook so NavbarRenderer and FooterRenderer reuse the
  // same in-flight request per parent_page_id. Intentionally not gated on
  // `showSubpagesColumn` so toggling it in the builder doesn't refetch.
  const { pages: footerPages } = useFooterPagesPublic(parentPageId, isPublic && !!parentPageId);

  // Derived values
  const { backgroundColor, textColor, mutedTextColor, borderColor } = useMemo(
    () =>
      resolveThemedSurface({
        mode: component.colorMode === "custom" ? "custom" : "primary",
        customColor: component.customColor,
        primaryColor,
        defaultCustomColor: DEFAULT_FOOTER_CUSTOM_COLOR,
      }),
    [component.colorMode, component.customColor, primaryColor]
  );

  const sectionLinks = useMemo(
    () => (component.showSectionsColumn ? getSectionLinks(allComponents) : []),
    [component.showSectionsColumn, allComponents]
  );

  // BuilderContext's `parentSlug` is the current page's slug, not the parent's,
  // when viewing a subpage. Derive the real parent slug from the fetched data.
  const trueParentSlug = useMemo(
    () => footerPages.find((p) => p.id === parentPageId)?.slug ?? null,
    [footerPages, parentPageId]
  );

  const columns = component.columns ?? [];
  const socialLinks = component.socialLinks ?? [];
  // Logo is rendered as an <img> on the public page — require HTTPS Supabase Storage URL
  // (not just any http(s) URL) to prevent attacker-controlled hosts, tracking pixels, and mixed-content.
  const hasLogo = !!component.logoUrl && isValidStorageUrl(component.logoUrl);
  const showPagesColumn = !!component.showSubpagesColumn && footerPages.length > 0 && !!trueParentSlug;
  const resortMap = getResortMapForLandingSlug(trueParentSlug);
  const hasAnyColumns = sectionLinks.length > 0 || columns.length > 0 || showPagesColumn;
  const alignClasses = FOOTER_ALIGNMENT_CLASSES[resolveFooterAlignment(component.alignment)];

  // Render
  return (
    <footer className="px-4 py-12" style={{ backgroundColor, color: textColor }} aria-label="Site footer">
      <div className="mx-auto flex max-w-5xl flex-col gap-10">
        {/* Top row: logo/tagline + link columns */}
        <div className={`flex flex-col gap-10 ${alignClasses.topRow}`}>
          {/* Brand block */}
          <div className={`flex max-w-sm flex-col gap-3 ${alignClasses.brand}`}>
            {hasLogo
              ? (() => {
                  const logoImg = (
                    <img
                      src={component.logoUrl}
                      alt="Logo"
                      height={32}
                      loading="lazy"
                      decoding="async"
                      className="h-auto max-h-8 w-auto max-w-[160px] object-contain"
                    />
                  );
                  // A configured logo link makes the footer logo clickable on the public page only.
                  return isPublic && component.logoLink ? (
                    <SmartLink
                      href={component.logoLink}
                      eventId={eventId}
                      primaryColor={primaryColor}
                      className="inline-flex w-fit transition-opacity hover:opacity-80"
                      ariaLabel="Go to linked page"
                    >
                      {logoImg}
                    </SmartLink>
                  ) : (
                    logoImg
                  );
                })()
              : null}
            {component.tagline ? (
              <p className="text-base font-medium" style={{ color: mutedTextColor }}>
                {component.tagline}
              </p>
            ) : null}
          </div>

          {/* Link columns */}
          {hasAnyColumns && (
            <div className="grid w-full flex-1 grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-8 lg:max-w-2xl">
              {component.showSectionsColumn && sectionLinks.length > 0 && (
                <div className="flex flex-col gap-3">
                  <h3 className="text-base font-semibold uppercase tracking-wider" style={{ color: textColor }}>
                    {component.sectionsColumnHeading || "Explore"}
                  </h3>
                  <div className="flex flex-col gap-2">
                    {sectionLinks.map((link) =>
                      isPublic ? (
                        <a
                          key={link.anchor}
                          href={`#${link.anchor}`}
                          className="touch-manipulation text-base font-medium transition-opacity hover:opacity-80"
                          style={{ color: mutedTextColor }}
                        >
                          {link.label}
                        </a>
                      ) : (
                        <span key={link.anchor} className="text-base font-medium" style={{ color: mutedTextColor }}>
                          {link.label}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}

              {showPagesColumn && trueParentSlug && (
                <div className="flex flex-col gap-3">
                  <h3 className="text-base font-semibold uppercase tracking-wider" style={{ color: textColor }}>
                    {component.subpagesColumnHeading || "Pages"}
                  </h3>
                  <div className="flex flex-col gap-2">
                    {footerPages.map((page) => {
                      const isParent = page.id === parentPageId;
                      // Short URL form — middleware rewrites to the long form internally so the
                      // address bar stays clean (e.g. `/yc-cancun/schedule`).
                      const href = isParent
                        ? `/${encodeURIComponent(trueParentSlug)}`
                        : `/${encodeURIComponent(trueParentSlug)}/${encodeURIComponent(page.slug)}`;
                      return isPublic ? (
                        <a
                          key={page.id}
                          href={href}
                          className="touch-manipulation text-base font-medium transition-opacity hover:opacity-80"
                          style={{ color: mutedTextColor }}
                        >
                          {page.name}
                        </a>
                      ) : (
                        <span key={page.id} className="text-base font-medium" style={{ color: mutedTextColor }}>
                          {page.name}
                        </span>
                      );
                    })}
                    {resortMap &&
                      (isPublic ? (
                        <a
                          href={resortMapSubpageHref(trueParentSlug)}
                          className="touch-manipulation text-base font-medium transition-opacity hover:opacity-80"
                          style={{ color: mutedTextColor }}
                        >
                          {resortMap.navLabel}
                        </a>
                      ) : (
                        <span className="text-base font-medium" style={{ color: mutedTextColor }}>
                          {resortMap.navLabel}
                        </span>
                      ))}
                  </div>
                </div>
              )}

              {columns.map((column) => (
                <div key={column.id} className="flex flex-col gap-3">
                  <h3 className="text-base font-semibold uppercase tracking-wider" style={{ color: textColor }}>
                    {column.heading || "Links"}
                  </h3>
                  <div className="flex flex-col gap-2">
                    {column.links.map((link) =>
                      isPublic && link.url && isSafeUrl(link.url) ? (
                        <a
                          key={link.id}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="touch-manipulation text-base font-medium transition-opacity hover:opacity-80"
                          style={{ color: mutedTextColor }}
                        >
                          {link.label || "Link"}
                        </a>
                      ) : (
                        <span key={link.id} className="text-base font-medium" style={{ color: mutedTextColor }}>
                          {link.label || "Link"}
                        </span>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom row: mandatory Nowadays AI copyright + optional socials */}
        <div
          className={`flex flex-col-reverse items-center gap-4 border-t pt-6 ${alignClasses.bottomRow}`}
          style={{ borderColor }}
        >
          <p className="text-base font-medium" style={{ color: mutedTextColor }}>
            © {new Date().getFullYear()} Nowadays AI
          </p>

          {socialLinks.length > 0 && (
            <div className="flex items-center gap-3">
              {socialLinks.map((link) => {
                const href = socialLinkHref(link);
                const isEmail = link.type === "email";
                const content = (
                  <span
                    className="flex h-9 w-9 items-center justify-center rounded-full border transition-opacity hover:opacity-80"
                    style={{ borderColor, color: textColor }}
                  >
                    <SocialIcon type={link.type} />
                  </span>
                );
                return isPublic && href ? (
                  <a
                    key={link.id}
                    href={href}
                    {...(isEmail ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                    className="touch-manipulation"
                    aria-label={link.type}
                  >
                    {content}
                  </a>
                ) : (
                  <span key={link.id}>{content}</span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
